import React, { useMemo, useCallback, useState, useEffect } from "react";
import { observer } from "mobx-react-lite";

import {
  ActionsSection,
  ButtonsSection,
} from "../../../components/data-components/sections/DataSections";
import {
  DeleteSewerObject,
  DeleteSewerObjectsFromAssignedProject,
  ManageDeletedData,
  ViewSewerObjects,
  ViewSewerObjectsFromAssignedProject,
} from "../../../services/authorization/Permissions";
import { ProjectMenuItemsModel } from "../ProjectMenuItems";
import PageLayout from "../../_layout/PageLayout";
import UploadFileToProject from "./components/UploadFileToProject";
import RunBatchVideoAnalysis from "./components/RunBatchVideoAnalysis";
import useErrorHandler, {
  ApplicationError,
} from "../../../services/error-handler/ErrorHandlerService";
import { DownloadSewerObjectsButtons } from "../../../components/buttons/download-sewer-objects/DownloadSewerObjectsButtons";
import { permissionsService } from "../../../services/authorization/UserPermissionsService";
import DeleteButton from "../../../components/buttons/delete-button/DeleteButton";
import { api } from "../../../services/api/ApiService";
import { ProjectSewerObjectsStorageModel } from "../../../services/sewer-object/ProjectSewerObjectsStorage";
import useUrlParams from "../../../services/url/UrlParamsService";
import { CurrentProjectStorageModel } from "../../../services/project/CurrentProjectStorage";
import { BadRequestError } from "../../../services/api/BadRequestError";
import DataGrid from "../../../components/datatable/DataGrid";
import { useHistory } from "react-router";
import { breadcrumbsStorage } from "../../../services/breadcrumbs/BreadcrumbsStorage";
import { EntityTypeFromPath } from "../../../services/breadcrumbs/EntityTypeFromPathModel";
import RunBatchHighlightDetection from "./components/RunBatchHighlightDetection";
import { featureFlagsStorage } from "../../../services/feature-flags/FeatureFlagsStorage";
import { Flags } from "../../../generated/FeatureFlags";

export default observer(() => {
  const history = useHistory();
  const urlParamsService = useUrlParams();
  const { handleErrorInSnackbar, handleErrorInModal } = useErrorHandler();

  const projectId = useMemo(() => urlParamsService.getProjectId()!, [urlParamsService]);

  const [currentProjectStorage] = useState(() =>
    CurrentProjectStorageModel.create({ projectId: projectId }, { api })
  );

  const [projectSewerObjectsStorage] = useState(() =>
    ProjectSewerObjectsStorageModel.create(
      {
        projectId: projectId,
        useGridFiltering: true,
      },
      { api }
    )
  );

  const projectMenuItems = useMemo(
    () =>
      ProjectMenuItemsModel.create({
        projectId: projectId,
        isLoading: currentProjectStorage.isLoading,
        numberOfSewerObjects: currentProjectStorage.currentProject.sewerObjectsCount,
      }),
    [
      projectId,
      currentProjectStorage.isLoading,
      currentProjectStorage.currentProject.sewerObjectsCount,
    ]
  );

  const reloadSewerObjects = useCallback(async () => {
    await projectSewerObjectsStorage.load();
  }, [projectSewerObjectsStorage]);

  const menuItems = {
    staticItems: projectMenuItems.getStaticItems(),
  };

  const isDeleteDisabled = useCallback(
    () =>
      !permissionsService.userHasOneOfThePermissions([
        DeleteSewerObject,
        DeleteSewerObjectsFromAssignedProject,
      ]) || !projectSewerObjectsStorage.selectedItemsCount,
    [projectSewerObjectsStorage.selectedItemsCount]
  );

  const deleteSelectedSewerObjects = useCallback(async () => {
    projectSewerObjectsStorage.setIsDeletingSewerObjects(true);
    try {
      await projectSewerObjectsStorage.deleteSewerObjects(projectSewerObjectsStorage.selectedItems);
    } catch (error) {
      const applicationError = error as ApplicationError;

      if (applicationError as BadRequestError) {
        applicationError.withModalTitleKey("general.sewer-objects.delete-error.title");
        handleErrorInModal(applicationError);
      } else {
        applicationError.withErrorMessage("default-error.sewer-objects.delete-from-project");
        handleErrorInSnackbar(applicationError);
      }
    }
    projectSewerObjectsStorage.setIsDeletingSewerObjects(false);
  }, [projectSewerObjectsStorage, handleErrorInModal, handleErrorInSnackbar]);

  useEffect(() => {
    breadcrumbsStorage.setEntityNames([
      {
        entity: EntityTypeFromPath.Project,
        name: currentProjectStorage.currentProject.name,
      },
    ]);

    breadcrumbsStorage.updateBreadcrumbs(history.location.pathname);
  }, [currentProjectStorage.currentProject.name, history.location.pathname]);

  return (
    <PageLayout
      accessWithOneOfThePermissions={[ViewSewerObjects, ViewSewerObjectsFromAssignedProject]}
      titleKey="ProjectSewerObjects.title"
      titleContext={{
        projectName:
          currentProjectStorage.currentProject && currentProjectStorage.currentProject.name,
      }}
      menuItems={menuItems}
    >
      <ActionsSection>
        <ButtonsSection>
          <UploadFileToProject projectId={projectId} reloadSewerObjectsMap={reloadSewerObjects} />
          <DownloadSewerObjectsButtons
            projectId={projectId}
            selectedSewerObjects={projectSewerObjectsStorage.selectedItems}
            allSewerObjectsCount={projectSewerObjectsStorage.allItemsCount}
            fileName={currentProjectStorage.currentProject.name}
          />
          <RunBatchVideoAnalysis selectedSewerObjects={projectSewerObjectsStorage.selectedItems} />
          {featureFlagsStorage.isFeatureEnabled(Flags.Show_Video_Segmentations) && (
            <RunBatchHighlightDetection
              selectedSewerObjects={projectSewerObjectsStorage.selectedItems}
            />
          )}
          <DeleteButton
            disabled={isDeleteDisabled()}
            confirmationAction={deleteSelectedSewerObjects}
            confirmationModalTitleKey="ProjectSewerObjects.delete.confirmation.title"
            confirmationModalTitleContext={{
              sewerObjectsCount: projectSewerObjectsStorage.selectedItemsCount,
            }}
            confirmationModalSubtitleKey="ProjectSewerObjects.delete.confirmation"
            canUserRestoreDeletedEntity={permissionsService.userHasPermissionTo(ManageDeletedData)}
          />
        </ButtonsSection>
      </ActionsSection>
      <DataGrid
        itemTypeKey="general.sewer-objects"
        dataGridStorage={projectSewerObjectsStorage}
        withTopSpacing
        customSelectAll
      />
    </PageLayout>
  );
});
